import { createAbortController, abortSignal, requestWasAborted, abortRequest } from "../utils/browserCompat";
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { listProductCategories, listProducts } from "../api/products";
import { useAuth } from "../context/AuthContext";
import { useLanding } from "../context/LandingContext";
import { useProjectConfig } from "../context/ProjectConfigContext";
import { getProductBackgroundImage } from "../utils/productMedia";
import { getCategoryLabel, normalizeCategoryMetadata } from "../utils/categoryAdapter";
import { appendConfiguredQuery, openConfiguredUrl } from "../utils/configuredNavigation";
import { safeButtonTextSx, safeTextSx, safeTitleSx, sectionTitleFontSize, textLength } from "../utils/textSafety";
import { scaleResponsiveCss, segmentVisualScale } from "../utils/segmentVisualStyle";
import { capitalizeSectionTitle } from "../utils/landingSectionText";
import {
  catalogListPath,
  normalizeCatalogMode,
  resolveCatalogModeFromSummary,
} from "../utils/catalogRouting";
import { isCanonicalGastronomicCatalogNavigation } from "../utils/siteExperienceDefaults";
import { buildGastronomicMenuGroups } from "../utils/gastronomicMenu";
import { resolveLandingMedia } from "../utils/landingMedia";


function cssColorWithOpacity(color, opacity = 1) {
  const candidate = String(color || "").trim();
  const alpha = Math.max(0, Math.min(1, Number.isFinite(Number(opacity)) ? Number(opacity) : 1));
  if (!candidate || candidate.toLowerCase() === "transparent" || alpha <= 0) return "transparent";
  if (alpha >= 0.999) return candidate;
  const match = candidate.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return candidate;
  const raw = match[1].length === 3 ? match[1].split("").map((char) => `${char}${char}`).join("") : match[1];
  const red = Number.parseInt(raw.slice(0, 2), 16);
  const green = Number.parseInt(raw.slice(2, 4), 16);
  const blue = Number.parseInt(raw.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

export default function PromotionsCarousel3() {
  const { pricingRevision } = useAuth();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down("md"));
  const { catalog, theme, navigation } = useLanding();
  const { gastronomicMenuEnabled } = useProjectConfig();
  const copy = catalog?.categories || {};
  const visual = copy;
  const cardBorderRadius = Number(visual.cardBorderRadius);
  const cardShadowOpacity = Number(visual.cardShadowOpacity);
  const cardShadowX = Number(visual.cardShadowX);
  const cardShadowY = Number(visual.cardShadowY);
  const cardShadowBlur = Number(visual.cardShadowBlur);
  const cardShadowSpread = Number(visual.cardShadowSpread);
  const fallbackAngle = Number(visual.fallbackAngle);
  const cardTitleShadowOpacity = Number(visual.cardTitleShadowOpacity);
  const cardTitleShadowX = Number(visual.cardTitleShadowX);
  const cardTitleShadowY = Number(visual.cardTitleShadowY);
  const cardTitleShadowBlur = Number(visual.cardTitleShadowBlur);
  const ctaBorderRadius = Number(visual.ctaBorderRadius);
  const gastronomicMode = catalog?.homeSource === "gastronomicMenu" && gastronomicMenuEnabled;
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [categoryData, setCategoryData] = useState({ categories: [], summary: {} });

  useEffect(() => {
    const controller = createAbortController();
    const configuredMode = normalizeCatalogMode(catalog?.mode);
    const requestedType = configuredMode === "products"
      ? "product"
      : configuredMode === "services"
        ? "service"
        : "";

    setLoading(true);
    setError(null);
    const request = gastronomicMode
      ? listProducts({
          gastronomicMenu: true,
          page: 1,
          pageSize: 500,
          sortBy: "gastronomicMenu",
          sortDir: "asc",
        }, { signal: abortSignal(controller) }).then((data) => ({ gastronomicItems: Array.isArray(data?.items) ? data.items : [], categories: [], summary: {} }))
      : listProductCategories({ tipo: requestedType, signal: abortSignal(controller) });

    request
      .then((data) => {
        if (!requestWasAborted(controller)) setCategoryData(data || { categories: [], summary: {} });
      })
      .catch((requestError) => {
        if (!requestWasAborted(controller)) {
          setError(String(copy.errorText ?? requestError?.message ?? "No se pudieron cargar las categorías."));
        }
      })
      .finally(() => {
        if (!requestWasAborted(controller)) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      });

    return () => abortRequest(controller);
  }, [catalog?.mode, copy.errorText, gastronomicMode, pricingRevision]);

  const resolvedMode = useMemo(
    () => gastronomicMode ? "products" : resolveCatalogModeFromSummary(catalog?.mode, categoryData?.summary),
    [catalog?.mode, categoryData?.summary, gastronomicMode],
  );
  const categories = useMemo(
    () => gastronomicMode
      ? buildGastronomicMenuGroups(categoryData?.gastronomicItems || [])
      : normalizeCategoryMetadata(categoryData)
          .filter((category) => category.count > 0)
          .map((category) => ({
            ...category,
            title: getCategoryLabel(category.value),
          }))
          .sort((a, b) => b.count - a.count || a.title.localeCompare(b.title, "es")),
    [categoryData, gastronomicMode],
  );

  const repeated = categories.length > 1 ? [...categories, ...categories] : categories;
  const cardWidth = isMobile ? 270 : isTablet ? 330 : 390;
  const duration = isMobile ? 30 : isTablet ? 38 : 46;
  const titleScale = segmentVisualScale(theme, "categories", "title");
  const imageScale = segmentVisualScale(theme, "categories", "image");
  const spacingScale = segmentVisualScale(theme, "categories", "spacing");
  const titleSize = scaleResponsiveCss(
    sectionTitleFontSize(copy.title, { xs: "2.15rem", md: "3rem" }),
    titleScale
  );
  const hasHeading = Boolean(copy.eyebrow || copy.title || copy.subtitle);
  const emptyText = String(copy.emptyText ?? "").trim();
  const initialLoading = loading && !hasLoadedOnce;
  const visibleError = error !== null && categories.length === 0;
  if (!initialLoading && visibleError && !error && !hasHeading) return null;
  if (!initialLoading && !visibleError && categories.length === 0 && !hasHeading && !emptyText) return null;

  const fallbackBackground = `linear-gradient(${visual.fallbackAngle}deg, ${visual.fallbackStartColor}, ${visual.fallbackEndColor})`;
  const overlayBackground = `linear-gradient(180deg, ${visual.overlayTopColor} 0%, ${visual.overlayMiddleColor} 38%, ${visual.overlayBottomColor} 100%)`;
  const cardShadow = `${cardShadowX}px ${cardShadowY}px ${cardShadowBlur}px ${cardShadowSpread}px ${cssColorWithOpacity(visual.cardShadowColor, cardShadowOpacity)}`;
  const titleShadow = `${cardTitleShadowX}px ${cardTitleShadowY}px ${cardTitleShadowBlur}px ${cssColorWithOpacity(visual.cardTitleShadowColor, cardTitleShadowOpacity)}`;
  const sectionBackgroundImage = resolveLandingMedia(visual.backgroundImageUrl);
  const sectionBackgroundCss = sectionBackgroundImage
    ? `url("${String(sectionBackgroundImage).replace(/"/g, "%22")}")`
    : "none";

  if (initialLoading) return null;

  const openCategory = (category) => {
    if (gastronomicMode) {
      openConfiguredUrl(`/carta?seccion=${encodeURIComponent(category.anchor)}`, navigate);
      return;
    }
    if (isCanonicalGastronomicCatalogNavigation(navigation, gastronomicMenuEnabled)) {
      navigate("/carta", { state: { menuCategory: category.title || category.value || "" } });
      return;
    }
    openConfiguredUrl(
      appendConfiguredQuery(
        catalogListPath(resolvedMode, navigation?.productsUrl || "/productos"),
        "categoria",
        category.value,
      ),
      navigate
    );
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, py: { xs: 5 * spacingScale, sm: 6 * spacingScale, md: 8 * spacingScale }, bgcolor: visual.backgroundColor, backgroundImage: sectionBackgroundCss, backgroundSize: "cover", backgroundPosition: visual.backgroundImagePosition, backgroundRepeat: "no-repeat", overflow: "hidden" }}>
      <Box sx={{ maxWidth: 1440, mx: "auto" }}>
        {hasHeading ? (
          <Box sx={{ mb: 3.5 }}>
            {copy.eyebrow ? <Typography data-landing-field="catalog.categories.eyebrow" variant="overline" sx={{ ...safeTextSx, color: visual.eyebrowColor }}>{copy.eyebrow}</Typography> : null}
            {copy.title ? <Typography data-landing-field="catalog.categories.title" variant="h3" sx={{ ...safeTitleSx, fontSize: titleSize, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)", color: visual.titleColor, mb: copy.subtitle ? 1 : 0 }}>{capitalizeSectionTitle(copy.title)}</Typography> : null}
            {copy.subtitle ? <Typography data-landing-field="catalog.categories.subtitle" sx={{ ...safeTextSx, color: visual.subtitleColor, maxWidth: 760, lineHeight: 1.7, whiteSpace: "pre-line" }}>{copy.subtitle}</Typography> : null}
          </Box>
        ) : null}

        {visibleError ? (
          error ? <Alert data-landing-field="catalog.categories.errorText" severity="error" sx={{ bgcolor: visual.errorBackgroundColor, color: visual.errorTextColor, border: `1px solid ${visual.errorBorderColor}`, "& .MuiAlert-icon": { color: visual.errorIconColor } }}>{error}</Alert> : null
        ) : categories.length === 0 ? (
          emptyText ? <Alert data-landing-field="catalog.categories.emptyText" severity="info" sx={{ bgcolor: visual.emptyBackgroundColor, color: visual.emptyTextColor, border: `1px solid ${visual.emptyBorderColor}`, "& .MuiAlert-icon": { color: visual.emptyIconColor } }}>{emptyText}</Alert> : null
        ) : (
          <Box
            sx={{
              position: "relative",
              overflowX: { xs: "auto", sm: "hidden" },
              overflowY: "hidden",
              mx: { xs: -2, sm: -3, md: -6 },
              px: { xs: 1, sm: 0 },
              py: 1.5,
              scrollSnapType: { xs: "x proximity", sm: "none" },
              overscrollBehaviorX: "contain",
              WebkitOverflowScrolling: "touch",
              maskImage: { xs: "none", sm: "linear-gradient(90deg, transparent 0%, black 7%, black 93%, transparent 100%)" },
              WebkitMaskImage: { xs: "none", sm: "linear-gradient(90deg, transparent 0%, black 7%, black 93%, transparent 100%)" },
              "@keyframes categoryMarquee": { "0%": { transform: "translate3d(0,0,0)" }, "100%": { transform: "translate3d(-50%,0,0)" } },
              "&:hover .category-track": { animationPlayState: "paused" },
            }}
          >
            <Box className="category-track" sx={{ display: "flex", width: "max-content", animation: { xs: "none", sm: categories.length > 1 ? `categoryMarquee ${duration}s linear infinite` : "none" }, willChange: { xs: "auto", sm: "transform" } }}>
              {repeated.map((category, index) => (
                <Box key={`${category.slug}-${index}`} sx={{ flex: "0 0 auto", width: cardWidth, px: 1, display: { xs: index < categories.length ? "block" : "none", sm: "block" }, scrollSnapAlign: { xs: "start", sm: "none" } }}>
                  <Card elevation={0} sx={{ position: "relative", height: { xs: 230 * imageScale, sm: 300 * imageScale, md: 325 * imageScale }, overflow: "hidden", bgcolor: visual.cardBackgroundColor, border: `1px solid ${visual.cardBorderColor}`, borderRadius: `${cardBorderRadius}px`, boxShadow: cardShadow, "&:hover .category-photo": { transform: "scale(1.055)" } }}>
                    <CardActionArea onClick={() => openCategory(category)} sx={{ height: "100%" }}>
                      {category.image ? (
                        <Box
                          className="category-photo"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundImage: getProductBackgroundImage(
                              category.image,
                              fallbackBackground,
                            ),
                            backgroundSize: visual.imageFit,
                            backgroundPosition: visual.imagePosition,
                            transition: "transform .5s ease",
                          }}
                        />
                      ) : (
                        <Box sx={{ position: "absolute", inset: 0, background: fallbackBackground }} />
                      )}
                      <Box sx={{ position: "absolute", inset: 0, background: overlayBackground }} />
                      <Box sx={{ position: "absolute", inset: 0, minWidth: 0, p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                        <Stack direction="row" spacing={1} alignItems="flex-end" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                          <Typography
                            variant="h4"
                            title={category.title}
                            sx={{
                              ...safeTextSx,
                              flex: "1 1 180px",
                              fontSize: textLength(category.title) > 52
                                ? { xs: "1.35rem", sm: "1.55rem", md: "1.75rem" }
                                : undefined,
                              color: visual.cardTitleColor,
                              textShadow: titleShadow,
                              display: "-webkit-box",
                              WebkitBoxOrient: "vertical",
                              WebkitLineClamp: 3,
                              overflow: "hidden",
                            }}
                          >
                            {category.title}
                          </Typography>
                          <Chip data-landing-field={category.count === 1 ? "catalog.categories.productSingular" : "catalog.categories.productPlural"} size="small" label={`${category.count} ${category.count === 1 ? (copy.productSingular ?? "producto") : (copy.productPlural ?? "productos")}`} sx={{ flexShrink: 1, maxWidth: "100%", bgcolor: visual.countBackgroundColor, color: visual.countTextColor, border: `1px solid ${visual.countBorderColor}` }} />
                        </Stack>
                        {copy.ctaLabel ? (
                          <Button data-landing-field="catalog.categories.ctaLabel" variant="contained" size="small" sx={{ ...safeButtonTextSx, mt: 1.5, alignSelf: "flex-start", maxWidth: "100%", borderRadius: `${ctaBorderRadius}px`, bgcolor: visual.ctaBackgroundColor, color: visual.ctaTextColor, border: `1px solid ${visual.ctaBorderColor}`, "&:hover": { bgcolor: visual.ctaHoverBackgroundColor, color: visual.ctaHoverTextColor } }} onClick={(event) => { event.stopPropagation(); openCategory(category); }}>
                            {copy.ctaLabel}
                          </Button>
                        ) : null}
                      </Box>
                    </CardActionArea>
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
